import detectMobileDevice from 'is-mobile';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { useConfig } from '../../hooks';
import Backdrop from '../Backdrop';
import IconButton from '../IconButton';
import UploadIcon from '../Icons/CloudUpload';
import CloseIcon from '../Icons/Times';
import WarningIcon from '../Icons/Warning';
import LoadingBar from '../LoadingBar';
import Modal from '../Modal';

const StyledBackdrop = styled(Backdrop)``;

const Uploader = ({ onClose, notification, isLoadingBar }) => {
  const isMobile = detectMobileDevice();
  const {
    fontFamily,
    header: { height, classicView },
    footer,
    window: { borderRadius, colors }
  } = useConfig();
  const { t } = useTranslation();

  return (
    <Root
      BackdropComponent={StyledBackdrop}
      viewClassicHeader={classicView}
      headerHeight={height}
      footerHeight={footer && footer.enabled ? footer.height : 0}
      borderRadius={borderRadius}
      isMobile={isMobile}
      isOpen
    >
      <Wrap textFontFamily={fontFamily} backgroundColor={colors.background}>
        <CloseButton onClick={onClose}>
          <CloseIcon fill={colors.accent} />
        </CloseButton>
        <InnerWrap>
          {notification && (
            <Notification textColor={colors.main} backgroundColor={colors.warning}>
              <WarningIcon />
              {t('uploaderError')}
            </Notification>
          )}

          <Content>
            <Input type="file" name="uploadFile" />
            <UploadIcon fill={colors.accent} />
            <div>
              <Text textColor={colors.accent}>
                {t('uploaderText')} &nbsp;
                <BrowseFile>{t('uploaderHighlightedText')}</BrowseFile>
              </Text>
              {/* change isLoadingBar's default props */}
              {isLoadingBar && <LoadingBar backgroundColor={colors.accent} />}
            </div>
          </Content>
        </InnerWrap>
      </Wrap>
    </Root>
  );
};

Uploader.propTypes = {
  /**
   * Function for close button of modal
   */
  onClose: PropTypes.func.isRequired,
  /**
   * If `true` (Default: `false`) a notification will be shown
   */
  notification: PropTypes.bool,
  /**
   * If `true` (Default: `false`) a LoadingBar-component will be shown
   */
  isLoadingBar: PropTypes.bool
};

Uploader.defaultProps = {
  notification: false,
  isLoadingBar: false
};

const Root = styled(Modal)`
  top: 0;
  height: 100%;
  padding: 20px;
  & ${StyledBackdrop} {
    background-color: rgba(0, 0, 0, 0.5);
  }
  ${({ isMobile }) => {
    if (isMobile) {
      return css`
        top: ${({ headerHeight }) => `-${headerHeight}px`};
        height: ${({ headerHeight, footerHeight }) =>
          `calc(100% + ${headerHeight}px + ${footerHeight}px)`};
      `;
    }
  }};
  ${({ viewClassicHeader }) => {
    if (viewClassicHeader) {
      return css`
        padding-top: 40px;
        padding-bottom: 40px;
      `;
    }
  }}
`;

const Wrap = styled.div`
  position: relative;
  flex: 1 0;
  height: 100%;
  padding: 20px;
  font-family: ${props => props.textFontFamily};
  background-color: ${props => props.backgroundColor};
  border-radius: 3px;
  box-shadow: 0 2px 15px 1px rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 999;
`;

const InnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fbfbfb;
  border: 1px dashed #ccc;
`;

const Notification = styled.div`
  padding: 2px 20px;
  font-size: 12px;
  font-weight: 900;
  line-height: 14px;
  color: ${props => props.textColor};
  background-color: ${props => props.backgroundColor};
  svg {
    position: relative;
    top: 2px;
    width: 12px;
    height: 12px;
    margin-right: 4px;
    fill: ${props => props.textColor};
  }
`;

const Content = styled.div`
  position: relative;
  flex: 1 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 6px;
  svg {
    width: 60px;
    height: 48px;
    margin-bottom: 16px;
  }
`;

const Input = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

const Text = styled.p`
  margin: 0 0 24px;
  font-size: 18px;
  font-weight: 400;
  line-height: 22px;
  text-align: center;
  color: ${props => props.textColor};
`;

const BrowseFile = styled.span`
  color: #52b6e8;
`;

export default Uploader;
